import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import qs from "qs";
import api from "@services/api";

/* ------------------------------------------------------------------ */
/* Subscription plans (ww_subscription_plan)                           */
/* ------------------------------------------------------------------ */

export function useSubscriptionPlansQuery(filters, page, pageSize) {
  return useQuery({
    queryKey: ["website", "subscription-plans", filters, page, pageSize],
    queryFn: async () => {
      const query = qs.stringify(
        { ...filters, page, page_size: pageSize },
        { encodeValuesOnly: true }
      );
      const { data } = await api.get(`/website/subscription-plans?${query}`);
      return data;
    },
    placeholderData: keepPreviousData
  });
}

export function useCreateSubscriptionPlanMutation() {
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post(`/website/subscription-plans`, payload);
      return data;
    }
  });
}

export function useSaveSubscriptionPlanMutation() {
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const { data } = await api.put(`/website/subscription-plans/${id}`, payload);
      return data;
    }
  });
}

export function useDeleteSubscriptionPlanMutation() {
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(`/website/subscription-plans/${id}`);
      return true;
    }
  });
}

/* ------------------------------------------------------------------ */
/* Customer subscriptions (ww_subscription)                            */
/* ------------------------------------------------------------------ */

export function useSubscriptionsQuery(filters, page, pageSize) {
  return useQuery({
    queryKey: ["website", "subscriptions", filters, page, pageSize],
    queryFn: async () => {
      const query = qs.stringify(
        { ...filters, page, page_size: pageSize },
        { encodeValuesOnly: true }
      );
      const { data } = await api.get(`/website/subscriptions?${query}`);
      return data;
    },
    placeholderData: keepPreviousData
  });
}

export function useSaveSubscriptionMutation() {
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const { data } = await api.patch(`/website/subscriptions/${id}`, payload);
      return data;
    }
  });
}

/* ------------------------------------------------------------------ */
/* On-site service scheduling (ww_service_config + day bookings)       */
/* ------------------------------------------------------------------ */

export function useServiceConfigQuery() {
  return useQuery({
    queryKey: ["website", "service-config"],
    queryFn: async () => {
      const { data } = await api.get(`/website/service-config`);
      return data;
    }
  });
}

export function useSaveServiceConfigMutation() {
  return useMutation({
    mutationFn: async ({ type, payload }) => {
      const { data } = await api.put(`/website/service-config/${type}`, payload);
      return data;
    }
  });
}

export function useServiceBookingsQuery(date, orderType) {
  return useQuery({
    queryKey: ["website", "service-bookings", date, orderType],
    queryFn: async () => {
      const query = qs.stringify(
        { date, order_type: orderType || undefined },
        { encodeValuesOnly: true }
      );
      const { data } = await api.get(`/website/service-bookings?${query}`);
      return data;
    },
    enabled: !!date,
    placeholderData: keepPreviousData
  });
}

/* ------------------------------------------------------------------ */
/* Shop products / variants (ww_product_variant)                       */
/* ------------------------------------------------------------------ */

export function useWebsiteProductsQuery() {
  return useQuery({
    queryKey: ["website", "products"],
    queryFn: async () => {
      const { data } = await api.get(`/website/products`);
      return data;
    }
  });
}

export function useProductVariantsQuery(productId) {
  return useQuery({
    queryKey: ["website", "variants", productId],
    queryFn: async () => {
      const { data } = await api.get(`/website/products/${productId}/variants`);
      return data;
    },
    enabled: !!productId
  });
}

export function useCreateVariantMutation() {
  return useMutation({
    mutationFn: async ({ productId, payload }) => {
      const { data } = await api.post(`/website/products/${productId}/variants`, payload);
      return data;
    }
  });
}

export function useSaveVariantMutation() {
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const { data } = await api.put(`/website/variants/${id}`, payload);
      return data;
    }
  });
}

export function useDeleteVariantMutation() {
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(`/website/variants/${id}`);
      return true;
    }
  });
}

/* ------------------------------------------------------------------ */
/* Shop product page rich content                                      */
/* (ww_product_detail / feature / dosage / review / qa)                */
/* ------------------------------------------------------------------ */

export function useProductContentQuery(productId) {
  return useQuery({
    queryKey: ["website", "product-content", productId],
    queryFn: async () => {
      const { data } = await api.get(`/website/products/${productId}/content`);
      return data;
    },
    enabled: !!productId
  });
}

export function useSaveProductDetailMutation() {
  return useMutation({
    mutationFn: async ({ productId, payload }) => {
      const { data } = await api.put(`/website/products/${productId}/detail`, payload);
      return data;
    }
  });
}

export function useCreateFeatureMutation() {
  return useMutation({
    mutationFn: async ({ productId, payload }) => {
      const { data } = await api.post(`/website/products/${productId}/features`, payload);
      return data;
    }
  });
}

export function useSaveFeatureMutation() {
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const { data } = await api.put(`/website/features/${id}`, payload);
      return data;
    }
  });
}

export function useDeleteFeatureMutation() {
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(`/website/features/${id}`);
      return true;
    }
  });
}

export function useSaveDosageMutation() {
  return useMutation({
    mutationFn: async ({ productId, rows }) => {
      const { data } = await api.put(`/website/products/${productId}/dosage`, { rows });
      return data;
    }
  });
}

export function useCreateReviewMutation() {
  return useMutation({
    mutationFn: async ({ productId, payload }) => {
      const { data } = await api.post(`/website/products/${productId}/reviews`, payload);
      return data;
    }
  });
}

export function useSaveReviewMutation() {
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const { data } = await api.put(`/website/reviews/${id}`, payload);
      return data;
    }
  });
}

export function useDeleteReviewMutation() {
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(`/website/reviews/${id}`);
      return true;
    }
  });
}

export function useCreateQaMutation() {
  return useMutation({
    mutationFn: async ({ productId, payload }) => {
      const { data } = await api.post(`/website/products/${productId}/qa`, payload);
      return data;
    }
  });
}

export function useSaveQaMutation() {
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const { data } = await api.put(`/website/qa/${id}`, payload);
      return data;
    }
  });
}

export function useDeleteQaMutation() {
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(`/website/qa/${id}`);
      return true;
    }
  });
}

/* ------------------------------------------------------------------ */
/* Product page placement (ww_product_placement)                       */
/* landing = homepage shop section, b2b = B2B catalogue, featured      */
/* ------------------------------------------------------------------ */

export function useProductPlacementsQuery() {
  return useQuery({
    queryKey: ["website", "product-placements"],
    queryFn: async () => {
      const { data } = await api.get(`/website/product-placements`);
      return data;
    }
  });
}

export function useSaveProductPlacementsMutation() {
  return useMutation({
    mutationFn: async ({ productId, placements }) => {
      const { data } = await api.put(`/website/products/${productId}/placements`, {
        placements
      });
      return data;
    }
  });
}

/* ------------------------------------------------------------------ */
/* B2B leads                                                           */
/* ------------------------------------------------------------------ */

export function useB2bLeadsQuery(kind, filters, page, pageSize) {
  // kind: "service-requests" | "inquiries" | "franchise-applications"
  return useQuery({
    queryKey: ["website", "b2b", kind, filters, page, pageSize],
    queryFn: async () => {
      const query = qs.stringify(
        { ...filters, page, page_size: pageSize },
        { encodeValuesOnly: true }
      );
      const { data } = await api.get(`/website/b2b/${kind}?${query}`);
      return data;
    },
    placeholderData: keepPreviousData
  });
}

export function useSaveB2bLeadStatusMutation() {
  return useMutation({
    mutationFn: async ({ kind, id, status }) => {
      const { data } = await api.patch(`/website/b2b/${kind}/${id}`, { status });
      return data;
    }
  });
}

/* ------------------------------------------------------------------ */
/* Order pre-service checklist (ww_order_checklist)                    */
/* ------------------------------------------------------------------ */

export function useOrderChecklistQuery(orderId) {
  return useQuery({
    queryKey: ["website", "order-checklist", orderId],
    queryFn: async () => {
      const { data } = await api.get(`/website/orders/${orderId}/checklist`);
      return data;
    },
    enabled: !!orderId
  });
}
